import React, { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  TrashIcon,
  PlusIcon,
  PencilIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useAppStore } from '../stores/useAppStore'
import { messageService, type Message, type MessageCount, type CreateMessage } from '../services/messageService'

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog'
import { Textarea } from '../components/ui/textarea'

const Messages: React.FC = () => {
  const { user, addNotification, selectedDate, setSelectedDate } = useAppStore()
  const queryClient = useQueryClient()
  const [isAddMessageOpen, setIsAddMessageOpen] = useState(false)
  const [newMessage, setNewMessage] = useState<CreateMessage>({
    messageText: '',
    assignedToID_FK: 0,
    creationDate: selectedDate
  })
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null)
  const [editingText, setEditingText] = useState('')

  // Validate user and technician ID
  const technicianId = user?.id ? Number(user.id) : null
  const isValidTechnician = technicianId && technicianId > 0

  // Initialize new message with current user ID and selected date
  useEffect(() => {
    if (isValidTechnician) {
      setNewMessage(prev => ({
        ...prev,
        assignedToID_FK: technicianId,
        creationDate: selectedDate
      }))
    }
  }, [isValidTechnician, technicianId, selectedDate])

  // Fetch messages for the technician
  const {
    data: messages = [],
    isLoading: loadingMessages,
    error: messagesError,
    refetch: refetchMessages
  } = useQuery<Message[]>({
    queryKey: ['technician-messages', technicianId, selectedDate],
    queryFn: () => {
      if (!isValidTechnician) {
        throw new Error('Invalid technician ID')
      }
      return messageService.getMessages(technicianId, selectedDate)
    },
    enabled: !!isValidTechnician,
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true,
    refetchInterval: 15000, // Reduced from 30s to 15s for faster updates
    staleTime: 0, // Data is immediately stale for aggressive refresh
  })

  // Fetch message count for the technician
  const {
    data: messageCount = { unreadCount: 0, readButNotCompletedCount: 0, totalCount: 0 },
    refetch: refetchMessageCount
  } = useQuery<MessageCount>({
    queryKey: ['technician-message-count', technicianId, selectedDate],
    queryFn: () => {
      if (!isValidTechnician) {
        throw new Error('Invalid technician ID')
      }
      return messageService.getMessageCount(technicianId, selectedDate)
    },
    enabled: !!isValidTechnician,
    retry: 2,
    refetchInterval: 15000, // Reduced from 30s to 15s for faster updates
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true,
    staleTime: 0, // Data is immediately stale for aggressive refresh
  })

  // Handle errors with useEffect
  useEffect(() => {
    if (messagesError) {
      console.error('Error fetching messages:', messagesError)
      addNotification({
        type: 'error',
        message: 'Failed to load messages. Please try again.',
        timestamp: Date.now()
      })
    }
  }, [messagesError, addNotification])

  const handleRetryLoadMessages = () => {
    if (refetchMessages) {
      refetchMessages()
      refetchMessageCount()
    }
  }

  const handleMarkAsCompleted = async (messageId: number, completed: boolean) => {
    try {
      await messageService.markAsCompleted(messageId, completed)
      refetchMessages()
      refetchMessageCount()
      // Invalidate notification and count queries for immediate update
      queryClient.invalidateQueries({ queryKey: ['technician-messages-notification'] })
      queryClient.invalidateQueries({ queryKey: ['message-count'] })
      addNotification({
        type: 'success',
        message: completed ? 'Message marked as completed' : 'Message marked as incomplete',
        timestamp: Date.now()
      })
    } catch (error) {
      console.error('Failed to mark message as completed:', error)
      addNotification({
        type: 'error',
        message: 'Failed to update message status',
        timestamp: Date.now()
      })
    }
  }

  const handleDeleteMessage = async (messageId: number) => {
    try {
      await messageService.deleteMessage(messageId)
      refetchMessages()
      refetchMessageCount()
      // Invalidate notification and count queries for immediate update
      queryClient.invalidateQueries({ queryKey: ['technician-messages-notification'] })
      queryClient.invalidateQueries({ queryKey: ['message-count'] })
      addNotification({
        type: 'success',
        message: 'Message deleted successfully',
        timestamp: Date.now()
      })
    } catch (error) {
      console.error('Failed to delete message:', error)
      addNotification({
        type: 'error',
        message: 'Failed to delete message',
        timestamp: Date.now()
      })
    }
  }

  const handleAddMessage = async () => {
    try {
      if (!newMessage.messageText.trim()) {
        addNotification({
          type: 'error',
          message: 'Message text is required',
          timestamp: Date.now()
        })
        return
      }

      await messageService.createMessage(newMessage)
      setNewMessage({
        messageText: '',
        assignedToID_FK: technicianId || 0,
        creationDate: selectedDate
      })
      setIsAddMessageOpen(false)
      refetchMessages()
      refetchMessageCount()
      // Invalidate notification and count queries for immediate update
      queryClient.invalidateQueries({ queryKey: ['technician-messages-notification'] })
      queryClient.invalidateQueries({ queryKey: ['message-count'] })
      addNotification({
        type: 'success',
        message: 'Message added successfully',
        timestamp: Date.now()
      })
    } catch (error) {
      console.error('Failed to add message:', error)
      addNotification({
        type: 'error',
        message: 'Failed to add message',
        timestamp: Date.now()
      })
    }
  }

  const handleStartEdit = (message: Message) => {
    setEditingMessageId(message.messageID)
    setEditingText(message.messageText)
  }

  const handleCancelEdit = () => {
    setEditingMessageId(null)
    setEditingText('')
  }

  const handleSaveEdit = async (messageId: number) => {
    try {
      if (!editingText.trim()) {
        addNotification({
          type: 'error',
          message: 'Message text cannot be empty',
          timestamp: Date.now()
        })
        return
      }

      await messageService.updateMessage(messageId, {
        messageText: editingText,
        hasBeenUpdated: true
      })
      
      setEditingMessageId(null)
      setEditingText('')
      refetchMessages()
      refetchMessageCount()
      // Invalidate notification and count queries for immediate update
      queryClient.invalidateQueries({ queryKey: ['technician-messages-notification'] })
      queryClient.invalidateQueries({ queryKey: ['message-count'] })

      addNotification({
        type: 'success',
        message: 'Message updated successfully',
        timestamp: Date.now()
      })
    } catch (error) {
      console.error('Failed to update message:', error)
      addNotification({
        type: 'error',
        message: 'Failed to update message',
        timestamp: Date.now()
      })
    }
  }

  // Handle invalid technician state
  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-red-600">Authentication Required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-gray-600">
              Please log in to access messages.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!isValidTechnician) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-red-600">Invalid Technician</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-gray-600">
              Unable to identify technician. Please log out and log in again.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

      return (
      <div className="min-h-screen bg-white p-4 md:p-6">
        <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">            
            <div>
              <h1 className="text-1xl md:text-2xl font-bold text-gray-900">
                Messages
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="selectedDate" className="text-sm font-medium">Date:</Label>
              <Input
                id="selectedDate"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto"
              />
            </div>
            
            <Dialog open={isAddMessageOpen} onOpenChange={(open) => {
              setIsAddMessageOpen(open)
              if (open) {
                // Ensure the message has the current selected date when dialog opens
                setNewMessage(prev => ({
                  ...prev,
                  creationDate: selectedDate
                }))
              }
            }}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <PlusIcon className="h-4 w-4" />
                  Add Message
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] bg-white">
                <DialogHeader>
                  <DialogTitle>Add New Message</DialogTitle>
                  <DialogDescription>
                    Create a new message for {new Date(selectedDate).toLocaleDateString()}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="messageText">Message Text</Label>
                    <Textarea
                      id="messageText"
                      placeholder="Enter your message here..."
                      value={newMessage.messageText}
                      onChange={(e) => setNewMessage({ ...newMessage, messageText: e.target.value })}
                      className="min-h-[100px]"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsAddMessageOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddMessage}>
                      Add Message
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {messagesError && (
              <Button onClick={handleRetryLoadMessages} variant="outline" size="sm">
                Retry
              </Button>
            )}
          </div>
        </div>

        {/* Messages Table */}
        <Card>
          <CardHeader>
            <CardTitle>Messages for {new Date(selectedDate).toLocaleDateString()}</CardTitle>
            <CardDescription>
              {Array.isArray(messages) ? messages.length : 0} message{(Array.isArray(messages) ? messages.length : 0) !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingMessages ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500">Loading messages...</p>
              </div>
            ) : messagesError ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <ExclamationTriangleIcon className="h-12 w-12 text-amber-500" />
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900">Unable to load messages</h4>
                  <p className="text-gray-500 mt-1">
                    {messagesError instanceof Error 
                      ? messagesError.message 
                      : 'There was a problem loading your messages. Please check your connection and try again.'}
                  </p>
                  <Button onClick={handleRetryLoadMessages} className="mt-3">
                    Try Again
                  </Button>
                </div>
              </div>
            ) : !Array.isArray(messages) || messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="rounded-full bg-gray-100 p-6">
                  📬
                </div>
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900">No messages</h4>
                  <p className="text-gray-500 mt-1">
                    You have no messages for {new Date(selectedDate).toLocaleDateString()}.
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-3 font-medium text-gray-900">Status</th>
                      <th className="text-left p-3 font-medium text-gray-900">Message</th>
                      <th className="text-left p-3 font-medium text-gray-900">Assigned To</th>
                      <th className="text-left p-3 font-medium text-gray-900">Created</th>
                      <th className="text-right p-3 font-medium text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((message) => {
                      const messageStatus = messageService.getMessageStatus(message)
                      return (
                        <tr key={message.messageID} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {message.completed ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Completed
                                </Badge>
                              ) : (
                                <Badge variant="outline" className={
                                  messageStatus === 'unread' 
                                    ? 'bg-blue-50 text-blue-700 border-blue-200' 
                                    : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                }>
                                  {messageStatus === 'unread' ? 'Unread' : 'Read'}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            {editingMessageId === message.messageID ? (
                              <div className="flex items-center gap-2">
                                <Textarea
                                  value={editingText}
                                  onChange={(e) => setEditingText(e.target.value)}
                                  className="min-h-[60px] text-sm"
                                  autoFocus
                                />
                                <div className="flex flex-col gap-1">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleSaveEdit(message.messageID)}
                                    className="h-8 px-2"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleCancelEdit}
                                    className="h-8 px-2"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between gap-2">
                                <p className={`text-sm leading-5 flex-1 ${
                                  messageStatus === 'unread' ? 'font-semibold' : ''
                                }`}>
                                  {message.messageText}
                                  {message.hasBeenUpdated && (
                                    <span className="ml-2 text-xs text-orange-600 italic">(updated)</span>
                                  )}
                                </p>
                                {!message.completed && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleStartEdit(message)}
                                    className="h-6 w-6 p-0"
                                    title="Edit message"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <UserIcon className="h-4 w-4" />
                              <span>{message.assignedToName}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <ClockIcon className="h-4 w-4" />
                              <span>{new Date(message.creationDate).toDateString()}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-2">
                              {!message.completed && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-3 text-xs"
                                  onClick={() => handleMarkAsCompleted(message.messageID, true)}
                                >
                                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                                  Mark Complete
                                </Button>
                              )}
                              {message.completed && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-3 text-xs"
                                  onClick={() => handleMarkAsCompleted(message.messageID, false)}
                                >
                                  Mark Incomplete
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-3 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                                onClick={() => handleDeleteMessage(message.messageID)}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default Messages 